import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollTop from './components/ScrollTop';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { VehicleProvider } from './contexts/VehicleContext';
import { Toaster } from './components/ui/toaster';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import './App.css';

// Every page except Home is its own chunk, so a visitor only downloads the code for the page they open.
const importers = {
  ProductListing: () => import('./pages/ProductListing'),
  ProductDetail: () => import('./pages/ProductDetail'),
  Cart: () => import('./pages/Cart'),
  Wishlist: () => import('./pages/Wishlist'),
  Compare: () => import('./pages/Compare'),
  Checkout: () => import('./pages/Checkout'),
  Orders: () => import('./pages/Orders'),
  Approvals: () => import('./pages/Approvals'),
  QuickOrder: () => import('./pages/QuickOrder'),
  Quotes: () => import('./pages/Quotes'),
  Account: () => import('./pages/Account'),
  Contact: () => import('./pages/Contact'),
  Blog: () => import('./pages/Blog'),
  CustomerLogin: () => import('./pages/CustomerLogin'),
  Shipping: () => import('./pages/info/Shipping'),
  Returns: () => import('./pages/info/Returns'),
  Faq: () => import('./pages/info/Faq'),
  Terms: () => import('./pages/info/Terms'),
  Privacy: () => import('./pages/info/Privacy'),
};
const L = Object.fromEntries(Object.entries(importers).map(([k, f]) => [k, lazy(f)]));
const named = (k, name) => lazy(() => importers[k]().then((m) => ({ default: m[name] })));
const OrderDetail = named('Orders', 'OrderDetail');
const ApprovalDetail = named('Approvals', 'ApprovalDetail');
const QuoteDetail = named('Quotes', 'QuoteDetail');

const PageFallback = () => (
  <div className="container mx-auto px-4 py-10" aria-busy="true" aria-label="Loading">
    <div className="h-8 w-56 bg-gray-200 rounded animate-pulse mb-6" />
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[...Array(4)].map((_, i) => <div key={i} className="h-56 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
  </div>
);

function App() {
  // after the first screen is up, quietly fetch the pages people usually open next
  useEffect(() => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 3000));
    const id = idle(() => { importers.ProductListing(); importers.ProductDetail(); });
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);
  return (
    <AuthProvider>
      <VehicleProvider>
        <CartProvider>
          <Router>
            <ScrollTop />
            <div className="flex flex-col min-h-screen">
              <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:bg-white focus:p-3 focus:text-red-600">Skip to content</a>
              <Header />
              <main id="main" className="flex-grow">
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/products" element={<L.ProductListing />} />
                    <Route path="/product/:slug" element={<L.ProductDetail />} />
                    <Route path="/cart" element={<L.Cart />} />
                    <Route path="/wishlist" element={<L.Wishlist />} />
                    <Route path="/compare" element={<L.Compare />} />
                    <Route path="/checkout" element={<L.Checkout />} />
                    <Route path="/orders" element={<L.Orders />} />
                    <Route path="/orders/:id" element={<OrderDetail />} />
                    <Route path="/approvals" element={<L.Approvals />} />
                    <Route path="/approvals/:id" element={<ApprovalDetail />} />
                    <Route path="/quick-order" element={<L.QuickOrder />} />
                    <Route path="/quotes/:id" element={<QuoteDetail />} />
                    <Route path="/quotes" element={<L.Quotes />} />
                    <Route path="/account" element={<L.Account />} />
                    <Route path="/blog" element={<L.Blog />} />
                    <Route path="/blog/:slug" element={<L.Blog />} />
                    <Route path="/contact" element={<L.Contact />} />
                    <Route path="/customer-login" element={<L.CustomerLogin />} />
                    <Route path="/shipping-information" element={<L.Shipping />} />
                    <Route path="/returns-exchanges" element={<L.Returns />} />
                    <Route path="/faq" element={<L.Faq />} />
                    <Route path="/terms-and-conditions" element={<L.Terms />} />
                    <Route path="/privacy-policy" element={<L.Privacy />} />
                    <Route path="*" element={<div className="container mx-auto px-4 py-16 text-center"><h1 className="text-3xl font-bold text-gray-900 mb-2">Page not found</h1><a href="/" className="text-red-600 font-semibold">Back to home</a></div>} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <Toaster />
            </div>
          </Router>
        </CartProvider>
      </VehicleProvider>
    </AuthProvider>
  );
}

export default App;